import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { select, Store } from '@ngxs/store';
import { endOfDay, startOfDay } from 'date-fns';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, skip } from 'rxjs/operators';
import { DashboardDepartmentChangeService } from 'src/app/core/services/dashboard-department-change.service';
import { MaintenanceJobStatus } from 'src/app/maintenance-user/enums/maintenance-job-status.enum';
import { IMaintenanceJobStatusResponse } from 'src/app/maintenance-user/models/maintenance-job-status-response.model';
import { LoadMaintenanceJobStatus } from 'src/app/maintenance-user/state/maintenance-job.actions';
import { MaintenanceJobState } from 'src/app/maintenance-user/state/maintenance-job.state';
import { getDepartmentId } from 'src/app/services/store-snapshot.service';
import { TimePeriod } from 'src/app/shared/dashboard/models/time-period.enum';
import { IdName } from 'src/app/shared/models/id-name';
import {
  WidgetBlock,
  WidgetBlockClickEvent,
} from 'src/app/shared/ui-elements/ui-widget-card/ui-widget-card.component';
import { ChangeCurrentSelectedDepartment } from 'src/app/tenant-selector/state/tenant-user-access.actions';
import { TenantUserAccessState } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { TenantUserPrivilegesService } from 'src/app/tenant-selector/services/tenant-user-privileges.service';
import { Privileges } from 'src/app/core/privileges/privileges';
import { UserMaintenanceRoutes } from '../../enums/routes.enum';

@Component({
  selector: 'app-maintenance-dashboard-widgets',
  templateUrl: './maintenance-dashboard-widgets.component.html',
  styleUrls: ['./maintenance-dashboard-widgets.component.scss'],
  standalone: false,
})
export class MaintenanceDashboardWidgetsComponent implements OnInit, OnDestroy {
  expiredJobsData: WidgetBlock[] = [
    { name: 'expiredJobs', value: 0 },
    { name: 'critical', value: 0, icon: 'error_outline', iconColor: 'red' },
  ];

  dueSoonJobsData: WidgetBlock[] = [
    { name: 'dueSoonJobs', value: 0 },
    { name: 'critical', value: 0, icon: 'warning_amber', iconColor: 'yellow' },
  ];

  postponedJobs = 0;
  inProgressJobs = 0;
  completedJobs = 0;

  loadingActions = [LoadMaintenanceJobStatus];

  vesselControl = new FormControl<string>('', { nonNullable: true });
  vesselOptions = select(TenantUserAccessState.getAllAvailableDepartments);

  periodControl = new FormControl(TimePeriod.Last30Days);
  periodOptions: IdName[] = [
    { id: TimePeriod.AllTime, name: 'allTime' },
    { id: TimePeriod.Last7Days, name: 'last7Days' },
    { id: TimePeriod.Last30Days, name: 'last30Days' },
    { id: TimePeriod.Last90Days, name: 'last90Days' },
    { id: TimePeriod.YearToDate, name: 'yearToDate' },
  ];

  customRangeControl = new FormControl<{ from: Date; to: Date } | null>(null);
  maxDate = new Date();

  private subscription = new Subscription();
  private suppressCustomRangeChange = false;

  constructor(
    private store: Store,
    private router: Router,
    private dashboardDepartmentChangeService: DashboardDepartmentChangeService,
    private tenantUserPrivileges: TenantUserPrivilegesService
  ) {}

  ngOnInit(): void {
    this.vesselControl.setValue(getDepartmentId(), { emitEvent: false });
    this.loadMaintenanceJobs();

    // Reload when vessel filter changes
    this.subscription.add(
      this.vesselControl.valueChanges.subscribe(() => {
        this.loadMaintenanceJobs();
      })
    );

    // Reload when period filter changes
    this.subscription.add(
      this.periodControl.valueChanges.subscribe((period) => {
        // Clear custom range when a predefined period is selected
        if (period !== TimePeriod.Custom) {
          this.clearCustomRangeFilters();
        }
        this.loadMaintenanceJobs();
      })
    );

    // Handle custom date range changes
    this.subscription.add(
      this.customRangeControl.valueChanges.subscribe((range) => {
        this.handleCustomRangeChange(range);
      })
    );

    // Reload data when tenant changes
    this.subscription.add(
      this.store
        .select(TenantUserAccessState.currentTenant)
        .pipe(
          filter((tenant) => !!tenant),
          distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
          skip(1) // Skip the initial emission to avoid duplicate loads on component init
        )
        .subscribe(() => {
          // Reset filters when tenant changes
          this.vesselControl.setValue(getDepartmentId(), { emitEvent: false });
          this.periodControl.setValue(TimePeriod.Last30Days, { emitEvent: false });
          this.clearCustomRangeFilters();
          // Note: Data reload happens in department change handler (tenant change triggers department change)
        })
    );

    // Reload data when department changes
    this.subscription.add(
      this.dashboardDepartmentChangeService.getDepartmentChanges().subscribe((departmentId) => {
        this.vesselControl.setValue(departmentId, { emitEvent: false });
        this.loadMaintenanceJobs();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private getDateRangeFromPeriod(period: string): { from?: string; to?: string } {
    if (!period || period === TimePeriod.AllTime) {
      return {};
    }

    if (period === TimePeriod.Custom) {
      const customRange = this.customRangeControl.value;
      return customRange?.from
        ? {
            from: startOfDay(new Date(customRange.from)).toISOString(),
            to: customRange.to ? endOfDay(new Date(customRange.to)).toISOString() : undefined,
          }
        : {};
    }

    if (period === TimePeriod.YearToDate) {
      const now = new Date();
      const yearStart = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
      return { from: yearStart.toISOString() };
    }

    const now = new Date();
    const startDate = new Date(now);

    switch (period) {
      case TimePeriod.Last7Days:
        startDate.setDate(now.getDate() - 7);
        break;
      case TimePeriod.Last30Days:
        startDate.setDate(now.getDate() - 30);
        break;
      case TimePeriod.Last90Days:
        startDate.setDate(now.getDate() - 90);
        break;
      default:
        return {};
    }

    startDate.setHours(0, 0, 0, 0);
    return { from: startDate.toISOString() };
  }

  private handleCustomRangeChange(range: { from: Date; to: Date } | null): void {
    if (this.suppressCustomRangeChange) {
      return;
    }

    if (range) {
      this.periodControl.setValue(TimePeriod.Custom, { emitEvent: false });
      this.loadMaintenanceJobs();
      return;
    }

    if (this.periodControl.value === TimePeriod.Custom) {
      this.periodControl.setValue(TimePeriod.Last30Days, { emitEvent: false });
      this.loadMaintenanceJobs();
    }
  }

  private clearCustomRangeFilters(): void {
    this.suppressCustomRangeChange = true;
    this.customRangeControl.setValue(null);
    this.suppressCustomRangeChange = false;
  }

  private loadMaintenanceJobs(): void {
    if (!this.tenantUserPrivileges.userHasPrivilege(Privileges.MaintenanceJobRead)) {
      return;
    }

    const departmentId = this.vesselControl.value;
    const dateRange = this.getDateRangeFromPeriod(
      this.periodControl.value || TimePeriod.Last30Days
    );

    this.store.dispatch(new LoadMaintenanceJobStatus([departmentId], dateRange.from, dateRange.to));

    this.subscription.add(
      this.store
        .select(MaintenanceJobState.getMaintenanceJobStatus)
        .subscribe((status: IMaintenanceJobStatusResponse | undefined) => {
          if (!status) {
            return;
          }

          this.expiredJobsData = [
            {
              name: 'expiredJobs',
              value: status.expiredJobs,
              tooltip: 'expiredJobsTooltip',
            },
            {
              name: 'critical',
              value: status.expiredJobsCritical,
              icon: 'error_outline',
              iconColor: 'red',
              tooltip: 'expiredJobsCriticalTooltip',
            },
          ];

          this.dueSoonJobsData = [
            {
              name: 'dueSoonJobs',
              value: status.expiringJobs,
              tooltip: 'dueSoonJobsTooltip',
            },
            {
              name: 'critical',
              value: status.expiringJobsCritical,
              icon: 'warning_amber',
              iconColor: 'yellow',
              tooltip: 'dueSoonJobsCriticalTooltip',
            },
          ];

          this.postponedJobs = status.postponedJobs;
          this.inProgressJobs = status.inProgressJobs;
          this.completedJobs = status.completedJobs;
        })
    );
  }

  onExpiredJobsBlockClick(event: WidgetBlockClickEvent): void {
    const isCritical = event.blockIndex === 1;
    this.navigateToJobsWithStatus(MaintenanceJobStatus.Expired, isCritical);
  }

  onDueSoonJobsBlockClick(event: WidgetBlockClickEvent): void {
    const isCritical = event.blockIndex === 1;
    this.navigateToJobsWithStatus(MaintenanceJobStatus.Expiring, isCritical);
  }

  openInProgressJobs(): void {
    this.navigateToJobsWithStatus(MaintenanceJobStatus.InProgress, false);
  }

  private navigateToJobsWithStatus(alertStatus: string, isCritical: boolean): void {
    const selectedVessels = this.vesselControl.value;
    const currentDepartmentId = this.store.selectSnapshot(
      TenantUserAccessState.currentDepartmentId
    );

    // If exactly one department is selected and it differs from current, change the department
    if (
      selectedVessels &&
      selectedVessels.length === 1 &&
      selectedVessels[0] !== currentDepartmentId
    ) {
      this.store.dispatch(new ChangeCurrentSelectedDepartment(selectedVessels[0], false));
    }

    // Navigate to the maintenance list with the alert status filter and optional critical filter
    const queryParams: any = { alertStatus };
    if (isCritical) {
      queryParams.isCriticalComponent = 'true';
    }

    this.router.navigate([UserMaintenanceRoutes.Main], { queryParams });
  }
}
